import {createFileRoute} from "@tanstack/react-router";
import {listCustomers} from "~/domainModels/ottokart/customers";
import {
    getProducts,
    listProductBrands,
    listProductCategories,
    listProductTypes,
    listVariants,
    type Product,
    type ProductBrand,
    type ProductCategory,
    type ProductType,
    type Variant,
} from "~/domainModels/ottokart/products";
import {getAdvancedQuotationById} from "~/domainModels/ottokart/quotations";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {ErrorComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/advanced-quotations/revise-quotation/-errorComponent";
import {NotFoundComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/advanced-quotations/revise-quotation/-notFoundComponent";
import {PendingComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/advanced-quotations/revise-quotation/-pendingComponent";
import {RouteComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/advanced-quotations/revise-quotation/-routeComponent";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottokart/_ottokartScaffold/admin/advanced-quotations/revise-quotation/$quotationId")({
    loader: async ({context, params}) => {
        const quotationIdResult = uuidZodValidator.safeParse(params.quotationId);
        if (!quotationIdResult.success) {
            throw new Error("Invalid quotation ID");
        }

        const {intellsysBaseUrl} = context;
        const workspaceId = context.workspace.id;
        const accessToken = context.authenticationStatus.accessToken;

        const [quotationResult, variantsResult, productsResult, productTypesResult, productBrandsResult, productCategoriesResult, customersResult] =
            await Promise.all([
                getAdvancedQuotationById({
                    intellsysBaseUrl,
                    workspaceId,
                    accessToken,
                    quotationId: quotationIdResult.data,
                }),
                listVariants({intellsysBaseUrl, workspaceId, accessToken}),
                getProducts({intellsysBaseUrl, workspaceId, accessToken}),
                listProductTypes({intellsysBaseUrl, workspaceId, accessToken}),
                listProductBrands({intellsysBaseUrl, workspaceId, accessToken}),
                listProductCategories({intellsysBaseUrl, workspaceId, accessToken}),
                listCustomers({intellsysBaseUrl, workspaceId, accessToken, listParams: {isVerified: true}}),
            ]);

        if (quotationResult.success !== true) throw quotationResult.error;
        const quotation = quotationResult.data;
        if (variantsResult.success !== true) throw variantsResult.error;
        if (productsResult.success !== true) throw productsResult.error;
        if (productTypesResult.success !== true) throw productTypesResult.error;
        if (productBrandsResult.success !== true) throw productBrandsResult.error;
        if (productCategoriesResult.success !== true) throw productCategoriesResult.error;
        if (customersResult.success !== true) throw customersResult.error;

        const variantsById = new Map<string, Variant>(variantsResult.data.map((variant) => [variant.id, variant]));
        const productsById = new Map<string, Product>(productsResult.data.map((product) => [product.id, product]));
        const productTypesById = new Map<string, ProductType>(productTypesResult.data.map((type) => [type.id, type]));
        const productBrandsById = new Map<string, ProductBrand>(productBrandsResult.data.map((brand) => [brand.id, brand]));
        const productCategoriesById = new Map<string, ProductCategory>(productCategoriesResult.data.map((category) => [category.id, category]));
        const customersById = new Map(customersResult.data.map((customer) => [customer.id, customer]));

        return {
            quotation,
            variantsById,
            productsById,
            productTypesById,
            productBrandsById,
            productCategoriesById,
            customersById,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
